import { environment } from "@/environments/environment";

const API_URL = environment.apiUrl.replace(/\/+$/, ""); // Remove trailing slashes if any
const AUTH_STORAGE_KEY = "lina.auth.session";
const API_DEBUG_PREFIX = "[LINA][api]";

type AuthEvent =
  | "INITIAL_SESSION"
  | "SIGNED_IN"
  | "SIGNED_OUT"
  | "TOKEN_REFRESHED"
  | "USER_UPDATED";
type AuthCallback = (event: AuthEvent, session: any | null) => void;

const authListeners = new Set<AuthCallback>();

function createRequestId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

function logApiStep(step: string, details?: unknown) {
  if (!environment.production) {
    console.log(`${API_DEBUG_PREFIX} ${step}`, details ?? "");
  }
}

function logApiError(step: string, details?: unknown) {
  if (!environment.production) {
    console.error(`${API_DEBUG_PREFIX} ${step}`, details ?? "");
  }
}

function summarizeBackendBody(body: unknown) {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    return { bodyType: typeof body };
  }

  const request = body as Partial<DataQueryRequest> & {
    args?: unknown;
  };

  if (request.resource) {
    return {
      resource: request.resource,
      operation: request.operation,
      parametersCount: request.parameters?.length ?? 0,
      hasBody: Boolean(request.body),
      single: Boolean(request.single),
      maybeSingle: Boolean(request.maybeSingle),
      head: Boolean(request.head),
    };
  }

  if ("args" in request) {
    return { hasRpcArgs: Boolean(request.args) };
  }

  return { keys: Object.keys(request) };
}

interface QueryParameter {
  key: string;
  value: string;
}

interface DataQueryRequest {
  resource: string;
  operation: string;
  select?: string;
  parameters?: QueryParameter[];
  body?: unknown;
  single?: boolean;
  maybeSingle?: boolean;
  head?: boolean;
  count?: string;
  onConflict?: string;
}

interface DataOperationResponse<T> {
  data: T | null;
  error: unknown;
  count: number | null;
}

interface QueryOptions {
  count?: string;
  head?: boolean;
}

interface OrderOptions {
  ascending?: boolean;
}

interface UpsertOptions {
  onConflict?: string;
}

function readStoredSession() {
  const raw = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
}

function writeStoredSession(session: any | null, event: AuthEvent) {
  if (session?.access_token) {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }

  authListeners.forEach((listener) => listener(event, session));
}

function normalizeSession(payload: any) {
  const session = payload?.session ?? (payload?.access_token ? payload : null);
  if (!session?.access_token) return null;

  return {
    ...session,
    expires_at:
      session.expires_at ??
      (session.expires_in
        ? Math.floor(Date.now() / 1000) + Number(session.expires_in)
        : undefined),
  };
}

async function readJson(response: Response) {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function normalizeError(body: unknown, status: number) {
  let error: unknown;

  if (body && typeof body === "object" && "error" in body) {
    error = (body as { error: unknown }).error;
  } else if (body && typeof body === "object" && "message" in body) {
    error = body;
  } else {
    error = {
      message: `Backend request failed with status ${status}`,
      details: body,
    };
  }

  if (error && typeof error === "object" && !Array.isArray(error)) {
    return {
      ...(error as Record<string, unknown>),
      status,
    };
  }

  return {
    message: String(error),
    details: error,
    status,
  };
}

function isUnauthorizedError(error: unknown) {
  return (
    Boolean(error) &&
    typeof error === "object" &&
    (error as { status?: unknown }).status === 401
  );
}

async function authRequest(path: string, options: RequestInit = {}) {
  const requestId = createRequestId();
  const startedAt = performance.now();

  try {
    const session = readStoredSession();
    const headers: Record<string, string> = {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...((options.headers as Record<string, string> | undefined) ?? {}),
    };

    if (session?.access_token) {
      headers["Authorization"] = `Bearer ${session.access_token}`;
    }

    logApiStep("auth request:start", {
      requestId,
      path,
      method: options.method ?? "GET",
      hasStoredSession: Boolean(session?.access_token),
    });

    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers,
    });
    const payload = await readJson(response);

    logApiStep("auth request:response", {
      requestId,
      path,
      status: response.status,
      ok: response.ok,
      durationMs: Math.round(performance.now() - startedAt),
    });

    if (!response.ok) {
      return { data: null, error: normalizeError(payload, response.status) };
    }

    return { data: payload, error: null };
  } catch (error) {
    logApiError("auth request:error", {
      requestId,
      path,
      message: getErrorMessage(error),
      durationMs: Math.round(performance.now() - startedAt),
    });
    return { data: null, error };
  }
}

const backendAuth = {
  async getSession() {
    const session = readStoredSession();
    if (!session?.access_token) {
      return { data: { session: null }, error: null };
    }

    const expiresAt = Number(session.expires_at ?? 0);
    if (
      session.refresh_token &&
      expiresAt > 0 &&
      expiresAt - 60 <= Math.floor(Date.now() / 1000)
    ) {
      const { data, error } = await authRequest("/api/auth/refresh", {
        method: "POST",
        body: JSON.stringify({ refreshToken: session.refresh_token }),
      });

      if (error) {
        if (isUnauthorizedError(error)) {
          writeStoredSession(null, "SIGNED_OUT");
          return { data: { session: null }, error };
        }
      } else {
        const refreshed = normalizeSession(data);
        if (refreshed) {
          writeStoredSession(refreshed, "TOKEN_REFRESHED");
          return { data: { session: refreshed }, error: null };
        }
      }
    }

    return { data: { session }, error: null };
  },

  async getUser() {
    const session = readStoredSession();
    if (!session?.access_token) {
      return { data: { user: null }, error: null };
    }

    const { data, error } = await authRequest("/api/auth/user", {
      method: "GET",
    });
    if (error) return { data: { user: null }, error };

    return { data: { user: (data as any)?.user ?? data }, error: null };
  },

  async updateUser(attributes: { email?: string }) {
    const { data, error } = await authRequest("/api/auth/user", {
      method: "PATCH",
      body: JSON.stringify(attributes),
    });

    if (error) return { data, error };

    const user = (data as any)?.user ?? data;
    const session = readStoredSession();
    if (session?.access_token && user) {
      writeStoredSession(
        {
          ...session,
          user: {
            ...(session.user ?? {}),
            ...user,
          },
        },
        "USER_UPDATED",
      );
    }

    return { data: { user }, error: null };
  },

  onAuthStateChange(callback: AuthCallback) {
    authListeners.add(callback);
    setTimeout(() => callback("INITIAL_SESSION", readStoredSession()), 0);

    return {
      data: {
        subscription: {
          unsubscribe: () => authListeners.delete(callback),
        },
      },
    };
  },

  async signInWithPassword(credentials: { email: string; password: string }) {
    const { data, error } = await authRequest("/api/auth/sign-in", {
      method: "POST",
      body: JSON.stringify(credentials),
    });

    if (error) return { data, error };

    const session = normalizeSession(data);
    writeStoredSession(session, "SIGNED_IN");
    return { data: { session, user: session?.user ?? null }, error: null };
  },

  async signUp(options: {
    email: string;
    password: string;
    options?: { data?: unknown };
  }) {
    const { data, error } = await authRequest("/api/auth/sign-up", {
      method: "POST",
      body: JSON.stringify({
        email: options.email,
        password: options.password,
        data: options.options?.data,
      }),
    });

    if (error) return { data, error };

    const session = normalizeSession(data);
    if (session) {
      writeStoredSession(session, "SIGNED_IN");
    }

    return {
      data: { session, user: (data as any)?.user ?? session?.user ?? null },
      error: null,
    };
  },

  async signOut() {
    const { error } = await authRequest("/api/auth/sign-out", {
      method: "POST",
    });
    writeStoredSession(null, "SIGNED_OUT");
    return { error };
  },
};

async function authHeaders(contentType = "application/json") {
  const headers: Record<string, string> = {};

  if (contentType) {
    headers["Content-Type"] = contentType;
  }

  const { data } = await backendAuth.getSession();
  const token = data.session?.access_token;
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
}

async function postBackend<T>(
  path: string,
  body: unknown,
): Promise<DataOperationResponse<T>> {
  const requestId = createRequestId();
  const startedAt = performance.now();

  try {
    logApiStep("data request:start", {
      requestId,
      path,
      ...summarizeBackendBody(body),
    });

    const response = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify(body),
    });

    const payload = await readJson(response);

    logApiStep("data request:response", {
      requestId,
      path,
      status: response.status,
      ok: response.ok,
      durationMs: Math.round(performance.now() - startedAt),
    });

    if (!response.ok) {
      return {
        data: null,
        error: normalizeError(payload, response.status),
        count: null,
      };
    }

    const result = payload as DataOperationResponse<T> | null;
    return {
      data: result?.data ?? null,
      error: result?.error ?? null,
      count: result?.count ?? null,
    };
  } catch (error) {
    logApiError("data request:error", {
      requestId,
      path,
      message: getErrorMessage(error),
      durationMs: Math.round(performance.now() - startedAt),
    });
    return { data: null, error, count: null };
  }
}

class BackendQueryBuilder<T = unknown> implements PromiseLike<
  DataOperationResponse<T>
> {
  private operation = "select";
  private selectColumns = "*";
  private parameters: QueryParameter[] = [];
  private body: unknown;
  private singleResult = false;
  private maybeSingleResult = false;
  private headOnly = false;
  private countOption?: string;
  private onConflictOption?: string;
  private mutation = false;

  constructor(private readonly resource: string) {}

  select(columns = "*", options?: QueryOptions) {
    this.selectColumns = columns || "*";
    this.countOption = options?.count;
    this.headOnly = Boolean(options?.head);

    if (!this.mutation) {
      this.operation = "select";
    }

    return this;
  }

  insert(values: unknown) {
    this.operation = "insert";
    this.body = values;
    this.mutation = true;
    return this;
  }

  update(values: unknown) {
    this.operation = "update";
    this.body = values;
    this.mutation = true;
    return this;
  }

  upsert(values: unknown, options?: UpsertOptions) {
    this.operation = "upsert";
    this.body = values;
    this.onConflictOption = options?.onConflict;
    this.mutation = true;
    return this;
  }

  delete() {
    this.operation = "delete";
    this.mutation = true;
    return this;
  }

  eq(column: string, value: unknown) {
    return this.filter(
      column,
      value === null ? "is" : "eq",
      value === null ? "null" : value,
    );
  }

  filter(column: string, operator: string, value: unknown) {
    this.parameters.push({
      key: column,
      value: `${operator}.${String(value)}`,
    });
    return this;
  }

  in(column: string, values: unknown[]) {
    this.parameters.push({
      key: column,
      value: `in.(${values.map(String).join(",")})`,
    });
    return this;
  }

  order(column: string, options?: OrderOptions) {
    const direction = options?.ascending === false ? "desc" : "asc";
    this.parameters.push({ key: "order", value: `${column}.${direction}` });
    return this;
  }

  limit(count: number) {
    this.parameters.push({ key: "limit", value: String(count) });
    return this;
  }

  single() {
    this.singleResult = true;
    return this;
  }

  maybeSingle() {
    this.maybeSingleResult = true;
    return this;
  }

  then<TResult1 = DataOperationResponse<T>, TResult2 = never>(
    onfulfilled?:
      | ((value: DataOperationResponse<T>) => TResult1 | PromiseLike<TResult1>)
      | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }

  private execute() {
    const request: DataQueryRequest = {
      resource: this.resource,
      operation: this.operation,
      select: this.selectColumns,
      parameters: this.parameters,
      body: this.body,
      single: this.singleResult,
      maybeSingle: this.maybeSingleResult,
      head: this.headOnly,
      count: this.countOption,
      onConflict: this.onConflictOption,
    };

    return postBackend<T>("/api/data/query", request);
  }
}

function storageBucket(bucket: string) {
  return {
    async upload(
      path: string,
      file: File,
      options?: { contentType?: string; upsert?: boolean },
    ) {
      const query = new URLSearchParams({
        path,
        upsert: options?.upsert ? "true" : "false",
      });

      try {
        const response = await fetch(
          `${API_URL}/api/storage/${encodeURIComponent(bucket)}/object?${query}`,
          {
            method: "POST",
            headers: await authHeaders(
              options?.contentType || file.type || "application/octet-stream",
            ),
            body: file,
          },
        );
        const payload = await readJson(response);

        if (!response.ok) {
          return {
            data: null,
            error: normalizeError(payload, response.status),
          };
        }

        const publicUrl =
          payload && typeof payload === "object" && "publicUrl" in payload
            ? String((payload as { publicUrl: unknown }).publicUrl)
            : undefined;

        return { data: { path, publicUrl }, error: null };
      } catch (error) {
        return { data: null, error };
      }
    },

    getPublicUrl(path: string) {
      const publicPath = path.split("/").map(encodeURIComponent).join("/");
      return {
        data: {
          publicUrl: `${API_URL}/api/storage/${encodeURIComponent(bucket)}/public/${publicPath}`,
        },
      };
    },
  };
}

export const backend: any = {
  auth: backendAuth,
  from(resource: string) {
    return new BackendQueryBuilder(resource);
  },
  rpc(functionName: string, args?: unknown) {
    return postBackend(`/api/data/rpc/${encodeURIComponent(functionName)}`, {
      args: args ?? {},
    });
  },
  storage: {
    from: storageBucket,
  },
};
